import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '@/utils/api';
// 2026-08-23 — employee-first identity: a login is a switch on an employee.
import { useEmployeeLoginSwitch } from './useEmployeeLoginSwitch';

// LBR2-C3 — Active / Past / All lifecycle segregation (wired to hrRoutes GET
// /employees?view=, which composes C1's pure resolveEmployeeLifecycleWhere).
export type LifecycleView = 'active' | 'past' | 'all';

type HrTab = 'employees' | 'teams';

interface Employee {
  id: string;
  employeeCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  designation?: string;
  departmentId?: string;
  department?: { id: string; name: string } | null;
  dateOfJoining?: string;
  salary?: number;
  panNumber?: string;
  status?: string;
  /**
   * 2026-08-24 — onboarding completeness, computed SERVER-side
   * (backend `employee-completeness.ts`) and sent only to the HR audience.
   * `undefined` therefore means "not entitled to know", which is why the chip
   * checks `=== false` rather than falsiness.
   */
  onboardingComplete?: boolean;
  onboardingMissing?: string[];
  isActive?: boolean;
  // 2026-08-23 — the linked login, attached per row by the employees list.
  // A missing key means "no login" (describeLoginCell treats both the same).
  user?: { id: string; isActive: boolean } | null;
  createdAt: string;
}

const INITIAL_FORM = {
  employeeCode: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  designation: '',
  departmentId: '',
  dateOfJoining: '',
  salary: '',
  panNumber: '',
};

export function usePeopleDirectory() {
  // Tab strip — defaults to Employees; URL-syncs via ?tab=teams.
  const [tab, setTab] = useState<HrTab>(() => {
    const q = new URLSearchParams(window.location.search).get('tab');
    return q === 'teams' ? 'teams' : 'employees';
  });
  useEffect(() => {
    const url = new URL(window.location.href);
    if (tab === 'teams') url.searchParams.set('tab', 'teams');
    else url.searchParams.delete('tab');
    window.history.replaceState({}, '', url.toString());
  }, [tab]);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  // LBR2-C3 — onboarding wizard (replaces the thin Add modal) + lifecycle view.
  const [showWizard, setShowWizard] = useState(false);
  // 2026-08-24 — Edit now opens the SAME wizard, prefilled. Manak's rule was
  // one-way: the full wizard moves into Edit; Add is never reduced to the old
  // one-screen drawer. The drawer below stays in the file but is unreachable
  // from the table, so nothing that still references its state breaks.
  const [editingEmployeeId, setEditingEmployeeId] = useState<string | null>(null);
  const [viewFilter, setViewFilter] = useState<LifecycleView>('active');

  // Edit Employee Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  // AI-vbof (Feature A) — `password` is admin-set/ reset only. Empty = no
  // change. Show/hide toggle mirrors the login form. Sent only when non-empty.
  const [editForm, setEditForm] = useState({ ...INITIAL_FORM, id: '', status: 'active', password: '' });
  const [showEditPassword, setShowEditPassword] = useState(false);


  // ── 2026-08-23 — multi-select delete ───────────────────────────────────────
  // 🔒 Selection is a set of ids, and it is CLEARED on every refetch (see
  // `fetchEmployees`). The list is server-paged, so a selection that outlived a
  // page change or a filter change would let the user delete people who are no
  // longer on screen — the one thing a bulk action must never do. Every action
  // below intersects the selection with what is currently rendered before
  // sending anything.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Outcome of the last bulk run, kept on screen (NOT an alert) because the
  // refusals are the part the user needs to read and act on.
  const [bulkReport, setBulkReport] = useState<
    { hidden: number; requested: number; kept: { name: string; reason: string }[] } | null
  >(null);

  const selectedVisible = employees.filter((e) => selectedIds.has(e.id));
  const selectedCount = selectedVisible.length;
  const allVisibleSelected = employees.length > 0 && selectedCount === employees.length;
  const someVisibleSelected = selectedCount > 0 && !allVisibleSelected;

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // 🔒 Select-all means the VISIBLE rows — this page, under the current filters
  // — never the whole table. The label next to it always states the count for
  // exactly that reason: "all" is ambiguous, "12 of 12 on this page" is not.
  const toggleAllVisible = () => {
    setSelectedIds(allVisibleSelected ? new Set() : new Set(employees.map((e) => e.id)));
  };

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      // LBR2-C3 — lifecycle view drives Active/Past/All; supersedes the legacy status filter.
      params.view = viewFilter;
      if (filterStatus) params.status = filterStatus;
      if (filterDepartment) params.departmentId = filterDepartment;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortDir = sortDir;
      params.page = String(page);
      params.limit = String(limit);
      const res = await api.get('/employees', { params });
      setEmployees(res.data.data?.employees ?? res.data.data ?? []);
      // 🔒 Drop the selection whenever the visible set changes (page, filters,
      // search, sort, or a post-delete refresh). Carrying it over would mean a
      // later "Delete 12 selected" acted on rows the user can no longer see.
      setSelectedIds(new Set());
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [search, viewFilter, filterStatus, filterDepartment, sortField, sortDir, page]);

  useEffect(() => {
    fetchEmployees();
    api.get('/departments').then(res => setDepartments(res.data.data ?? [])).catch(() => {});
  }, [fetchEmployees]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/employees', {
        ...form,
        salary: form.salary ? parseFloat(form.salary) : undefined,
        departmentId: form.departmentId || undefined,
        panNumber: form.panNumber || undefined,
        phone: form.phone || undefined,
      });
      setShowModal(false);
      setForm(INITIAL_FORM);
      fetchEmployees();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create employee');
    } finally {
      setSaving(false);
    }
  };

  // LBR2-C2 — GUARDED delete (SEPARATE from Exit=archive). Segregates deletable
  // (wrongly-created, no activity) → front-end HIDE (reversible, no DB purge) vs
  // NOT deletable (has real data) → BLOCKED, steered to Exit. Two-phase: preview
  // the classification, then confirm only for a hide.
  const handleDelete = async (id: string) => {
    try {
      const preview = await api.get(`/hr-lifecycle/employees/${id}/deletability`);
      const { deletable, action, message } = preview.data?.data ?? {};
      if (!deletable || action === 'blocked') {
        // Real data present — hard delete is blocked. Inform + steer to Exit.
        alert(message || 'This employee has activity data and cannot be deleted. Use Exit to archive them instead.');
        return;
      }
      if (!confirm(message || 'This will hide this employee (reversible, no data deleted). Continue?')) return;
      await api.post(`/hr-lifecycle/employees/${id}/delete`, { confirm: true });
      fetchEmployees();
    } catch (err: any) {
      alert(err.response?.data?.error || err.response?.data?.message || 'Failed to delete employee');
    }
  };

  /**
   * 2026-08-23 — BULK delete for the multi-select.
   *
   * One request, not N: `POST /hr-lifecycle/employees/bulk-delete` runs the same
   * per-employee guard the single delete runs and answers with a verdict for
   * every id. Same two-phase contract — preview first so the confirm can name
   * the real numbers, then confirm.
   *
   * 🔒 PARTIAL FAILURE IS THE POINT. The guard refuses anyone holding real
   * activity, so a selection of ten commonly deletes seven. This must never
   * read as "10 deleted", and one refusal must never cancel the other nine —
   * so the refusals are listed BY NAME with their reason, on screen, after the
   * run. The names are read from the current rows before the refresh clears them.
   */
  const handleBulkDelete = async () => {
    // Intersect with what is on screen — never trust the selection alone.
    const targets = employees.filter((e) => selectedIds.has(e.id));
    if (targets.length === 0) return;
    const nameOf = new Map(targets.map((e) => [e.id, `${e.firstName} ${e.lastName}`.trim() || e.employeeCode]));
    const ids = targets.map((e) => e.id);

    try {
      setBulkBusy(true);
      setBulkReport(null);

      const preview = await api.post('/hr-lifecycle/employees/bulk-delete', { ids });
      const { deletable = 0, blocked = 0 } = preview.data?.data ?? {};

      const lines = [`Delete ${ids.length} selected employee${ids.length === 1 ? '' : 's'}?`];
      if (blocked > 0) {
        lines.push(
          '',
          `${blocked} of them cannot be deleted — they hold real activity (deals, invoices, jobs) and will be KEPT.`,
          `${deletable} will be hidden. Hiding is reversible; no data is deleted.`,
        );
      } else {
        lines.push('', 'Hiding is reversible; no data is deleted.');
      }
      if (!confirm(lines.join('\n'))) return;

      const res = await api.post('/hr-lifecycle/employees/bulk-delete', { ids, confirm: true });
      const data = res.data?.data ?? {};
      const kept = (data.results ?? [])
        .filter((r: any) => r.status !== 'hidden')
        .map((r: any) => ({
          name: nameOf.get(r.id) ?? r.id,
          reason:
            r.status === 'refused'
              ? r.message || 'Has activity data — use Exit to archive instead.'
              : r.status === 'not_found'
                ? 'No longer exists.'
                : r.message || 'Failed.',
        }));
      setBulkReport({ hidden: data.hidden ?? 0, requested: data.requested ?? ids.length, kept });
      fetchEmployees();
    } catch (err: any) {
      alert(err.response?.data?.error || err.response?.data?.message || 'Failed to delete the selected employees');
    } finally {
      setBulkBusy(false);
    }
  };

  const openEditModal = (emp: Employee) => {
    setEditForm({
      id: emp.id,
      employeeCode: emp.employeeCode || '',
      firstName: emp.firstName || '',
      lastName: emp.lastName || '',
      email: emp.email || '',
      phone: emp.phone || '',
      designation: emp.designation || '',
      departmentId: emp.departmentId || emp.department?.id || '',
      dateOfJoining: emp.dateOfJoining ? emp.dateOfJoining.split('T')[0] : '',
      salary: emp.salary != null ? String(emp.salary) : '',
      panNumber: emp.panNumber || '',
      status: emp.status || (emp.isActive !== false ? 'active' : 'inactive'),
      password: '',
    });
    setShowEditModal(true);
    setShowEditPassword(false);
  };

  // 2026-08-23 — the login switch. Its fetch/toggle/race logic now lives in
  // useEmployeeLoginSwitch (2026-08-24) so the onboarding wizard can render the
  // same control; this page is unchanged in behaviour and the 12 races in
  // EmployeesPage.loginswitch.test.tsx still pin it from here.
  const {
    loginState, loginLoading, loginBusy, loginError, loginForbidden, toggleLogin,
  } = useEmployeeLoginSwitch({
    employeeId: showEditModal ? editForm.id || null : null,
    open: showEditModal,
    onChanged: fetchEmployees,
  });

  const handleEditEmployee = async () => {
    try {
      setEditSaving(true);
      await api.put(`/employees/${editForm.id}`, {
        employeeCode: editForm.employeeCode,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        phone: editForm.phone || undefined,
        designation: editForm.designation || undefined,
        departmentId: editForm.departmentId || undefined,
        dateOfJoining: editForm.dateOfJoining || undefined,
        salary: editForm.salary ? parseFloat(editForm.salary) : undefined,
        panNumber: editForm.panNumber || undefined,
        status: editForm.status,
        // AI-vbof — only send password when the admin typed one. Empty = no change.
        ...(editForm.password && editForm.password.trim().length > 0 ? { password: editForm.password } : {}),
      });
      setShowEditModal(false);
      setEditForm((f) => ({ ...f, password: '' }));
      fetchEmployees();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update employee');
    } finally {
      setEditSaving(false);
    }
  };

  return { tab, setTab, employees, setEmployees, loading, setLoading, error, setError, search, setSearch, filterStatus, setFilterStatus, filterDepartment, setFilterDepartment, departments, setDepartments, showFilters, setShowFilters, sortField, setSortField, sortDir, setSortDir, page, setPage, total, setTotal, limit, totalPages, showModal, setShowModal, saving, setSaving, form, setForm, showWizard, setShowWizard, editingEmployeeId, setEditingEmployeeId, viewFilter, setViewFilter, showEditModal, setShowEditModal, editSaving, setEditSaving, editForm, setEditForm, showEditPassword, setShowEditPassword, selectedIds, setSelectedIds, bulkBusy, setBulkBusy, bulkReport, setBulkReport, selectedVisible, selectedCount, allVisibleSelected, someVisibleSelected, toggleOne, toggleAllVisible, fetchEmployees, handleSort, SortIcon, handleCreate, handleDelete, handleBulkDelete, openEditModal, loginState, loginLoading, loginBusy, loginError, loginForbidden, toggleLogin, handleEditEmployee };
}
export type PeopleDirectoryState = ReturnType<typeof usePeopleDirectory>;
